import React, { useState, useEffect } from 'react';
import { addLibraryVideo, getMembers } from '../services/api';
import MemberPicker from './MemberPicker';
import NewSpeakerFields from './NewSpeakerFields';

const inputStyle = {
  background: 'var(--bg-input)',
  border: '1px solid var(--border-accent)',
  color: 'var(--text-primary)',
  outline: 'none',
};

const EMPTY_SPEAKER = { email: '', first_name: '', last_name: '' };

/**
 * AddLibraryVideoModal — admin modal for adding a video to the shared Library
 * and attributing it to a speaker. No meeting/slot attached.
 *
 * The speaker is either an existing club member or someone new: typing a name
 * that isn't in the club surfaces an "invite as a new speaker" row, which
 * creates a provisional member and emails them an invitation.
 *
 * Props:
 *   onCreated {fn}  () => void — called after the admin dismisses the result
 *   onClose   {fn}  () => void — called on cancel or backdrop click
 */
export default function AddLibraryVideoModal({ onCreated, onClose }) {
  const [title, setTitle] = useState('');
  const [synopsis, setSynopsis] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');

  const [members, setMembers] = useState([]);
  const [speakerId, setSpeakerId] = useState('');
  const [newSpeaker, setNewSpeaker] = useState(null); // null = picking an existing member

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    // No status filter: provisional speakers are 'pending', and a previously
    // added one must stay pickable for their next video.
    getMembers()
      .then(res => setMembers(res?.members || []))
      .catch(() => setMembers([]));
  }, []);

  // Prefill from whatever the admin typed into the picker: an address goes to
  // email, otherwise split the name on the first space so they usually only
  // have one box left to fill.
  const startNewSpeaker = (query) => {
    setSpeakerId('');
    if (query.includes('@')) {
      setNewSpeaker({ ...EMPTY_SPEAKER, email: query });
      return;
    }
    const [first, ...rest] = query.split(/\s+/).filter(Boolean);
    setNewSpeaker({ ...EMPTY_SPEAKER, first_name: first || '', last_name: rest.join(' ') });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!newSpeaker && !speakerId) {
      setError('Pick a speaker, or add someone new.');
      return;
    }

    setLoading(true);
    try {
      const res = await addLibraryVideo({
        title: title.trim(),
        synopsis: synopsis.trim() || undefined,
        youtube_url: youtubeUrl.trim(),
        ...(newSpeaker
          ? {
              new_speaker: {
                email: newSpeaker.email.trim(),
                first_name: newSpeaker.first_name.trim(),
                last_name: newSpeaker.last_name.trim() || undefined,
              },
            }
          : { speaker_id: speakerId }),
      });

      // A new speaker produces an invite the admin may need to act on, so show
      // the outcome rather than closing silently.
      if (res?.invite) setResult(res);
      else onCreated();
    } catch (err) {
      setError(errorMessage(err));
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'var(--bg-overlay)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-md rounded-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto"
        style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-accent)' }}
      >
        {result ? (
          <ResultPanel result={result} onDone={onCreated} />
        ) : (
          <>
            <div>
              <h3 className="font-display text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                Add a video to the Library
              </h3>
              <p className="text-xs mt-0.5 font-medium" style={{ color: 'var(--text-faint)' }}>
                Visible to everyone in the club
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
                  Speaker <span style={{ color: 'var(--accent)' }}>*</span>
                </label>
                {newSpeaker ? (
                  <NewSpeakerFields
                    value={newSpeaker}
                    onChange={setNewSpeaker}
                    onCancel={() => setNewSpeaker(null)}
                  />
                ) : (
                  <MemberPicker
                    members={members}
                    value={speakerId}
                    onChange={setSpeakerId}
                    placeholder="Search members, or add someone new…"
                    allowCreate
                    onCreate={startNewSpeaker}
                  />
                )}
              </div>

              <div>
                <label htmlFor="library-title" className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
                  Title <span style={{ color: 'var(--accent)' }}>*</span>
                </label>
                <input
                  id="library-title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={300}
                  required
                  placeholder="e.g. Designing for Trust"
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={inputStyle}
                />
              </div>

              <div>
                <label htmlFor="library-url" className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
                  YouTube link <span style={{ color: 'var(--accent)' }}>*</span>
                </label>
                <input
                  id="library-url"
                  type="url"
                  value={youtubeUrl}
                  onChange={(e) => setYoutubeUrl(e.target.value)}
                  required
                  placeholder="https://youtube.com/watch?v=…"
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={inputStyle}
                />
              </div>

              <div>
                <label htmlFor="library-synopsis" className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
                  Description
                </label>
                <textarea
                  id="library-synopsis"
                  value={synopsis}
                  onChange={(e) => setSynopsis(e.target.value)}
                  maxLength={1000}
                  rows={3}
                  placeholder="A sentence or two about the video (optional)"
                  className="w-full px-3 py-2 rounded-lg text-sm resize-none"
                  style={inputStyle}
                />
              </div>

              {error && (
                <p className="text-sm" style={{ color: 'var(--color-danger)' }}>⚠ {error}</p>
              )}

              <div className="flex gap-2 justify-end pt-1">
                <button type="button" onClick={onClose} className="pill-tab text-sm" style={{ color: 'var(--text-muted)' }}>
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="btn-bouncy text-sm px-5 py-2">
                  {loading ? 'Adding…' : 'Add video'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function errorMessage(err) {
  switch (err.code) {
    case 'INVALID_EMAIL':      return "That email doesn't look right.";
    case 'EMAIL_REQUIRED':     return 'An email is required to invite a new speaker.';
    case 'NAME_REQUIRED':      return "Enter the speaker's first name.";
    case 'SPEAKER_NOT_FOUND':  return 'That speaker is no longer in the club. Pick someone else.';
    case 'SPEAKER_REQUIRED':   return 'Pick a speaker, or add someone new.';
    case 'MISSING_FIELDS':     return 'Title and YouTube link are required.';
    case 'RATE_LIMIT_EXCEEDED': return "You've hit the daily limit for adding videos.";
    default:                   return err.message || 'Failed to add video';
  }
}

/** Outcome of a create that involved a new speaker — the admin may need the link. */
function ResultPanel({ result, onDone }) {
  const { speaker, invite } = result;
  const name = `${speaker?.first_name || ''} ${speaker?.last_name || ''}`.trim() || speaker?.email;

  const messages = {
    created: invite.emailSent
      ? `Video added. We emailed an invite to ${speaker.email}.`
      : `Video added, but the invite email didn't send. Copy this link and share it:`,
    existing: `Video added. ${speaker.email} already had a pending invite — here's the link:`,
    skipped_member: `Video added and attributed to ${name}, who's already a member.`,
    skipped_inactive: `Video added and attributed to ${name}, who is deactivated. Reactivate them in Members if that's wrong.`,
    failed: `Video added, but the invite couldn't be created (${invite.error}). Invite them from Admin → Members.`,
  };

  return (
    <div className="space-y-4">
      <h3 className="font-display text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
        Video added
      </h3>
      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
        {messages[invite.status] || messages.created}
      </p>

      {invite.inviteUrl && (
        <div>
          <label htmlFor="invite-link" className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
            Invite link
          </label>
          <input
            id="invite-link"
            type="text"
            readOnly
            value={invite.inviteUrl}
            onClick={(e) => e.target.select()}
            className="w-full px-3 py-2 rounded-lg text-xs font-mono"
            style={inputStyle}
          />
        </div>
      )}

      <div className="flex justify-end pt-1">
        <button type="button" onClick={onDone} className="btn-bouncy text-sm px-5 py-2">
          Done
        </button>
      </div>
    </div>
  );
}
